import {
    aggregateNetExplosiveMassInG,
    calculateVolume,
    calculateWeight,
} from "@/components/TransportOrder/CSVExport/csvExportHelpers"
import { isCargoFreight, type Article, type HandlingUnit, type Shipment } from "@/model/transportorder/transportOrder"
import { normalizeToMmt } from "@/model/transportorder/measurement"
import { getClientTimezone, getTimezoneOfCountry } from "@/timezones/timezones"
import { formatInboundCsvDate, formatInboundCsvTime } from "./inboundCsvFormatting"
import type { InboundCsvHeader } from "./inboundCsvSerializer"
import { pullUpArticles } from "@/model/transportorder/transportOrderFlattening"

export type InboundMaterialCsvExportRow = {
    transportOrderNumber?: string
    externalOrderCreatedDate?: string
    pickupDate?: string
    pickupFrom?: string
    pickupTo?: string
    deliveryDate?: string
    shipFromName?: string
    shipFromAddressCountry?: string
    shipFromAddressPostalCode?: string
    shipFromAddressCity?: string
    shipFromAddressStreet?: string
    shipFromDuns?: string
    shipFromSupplierCode?: string
    deliveringFreightForwarderName?: string
    deliveringFreightForwarderAddressCountry?: string
    deliveringFreightForwarderAddressPostalCode?: string
    deliveringFreightForwarderAddressCity?: string
    deliveringFreightForwarderAddressStreet?: string
    deliveringFreightForwarderDuns?: string
    deliveringFreightForwarderSupplierCode?: string
    calculatedArrivalAtCrossDock?: string
    shipToName?: string
    shipToAddressCountry?: string
    shipToAddressPostalCode?: string
    shipToAddressCity?: string
    shipToAddressStreet?: string
    shipToDuns?: string
    shipToSupplierCode?: string
    shipToPlantCode?: string
    shipToPlaceOfDischarge?: string
    productNumbers?: string
    lengthHandlingUnit?: number
    widthHandlingUnit?: number
    heightHandlingUnit?: number
    totalNumberOfPieces?: string
    handlingUnitQuantity?: number
    outerPackagingMaterialId?: string
    unDangerousGoodsCode?: string
    descriptionDangerousGoods?: string
    dangerousGoodsClass?: string
    dangerousGoodsPackingGroup?: string
    netExplosiveWeight?: number
    customsGoods?: string
    grossWeight?: number
    grossVolume?: number
    stackingFactor?: number
}

/**
 * Property order is important here, it will define the order in the csv.
 */
const MATERIAL_HEADER_LABELS: Record<keyof InboundMaterialCsvExportRow, string> = {
    transportOrderNumber: "Transport order number",
    externalOrderCreatedDate: "Order date",
    pickupDate: "Requested pickup date at ship-from",
    pickupFrom: "From",
    pickupTo: "To",
    deliveryDate: "Calculated delivery date at ship-to",
    shipFromName: "Ship-from name",
    shipFromAddressCountry: "Ship-from country",
    shipFromAddressPostalCode: "Ship-from zip code",
    shipFromAddressCity: "Ship-from city",
    shipFromAddressStreet: "Ship-from street",
    shipFromDuns: "Ship-from DUNS",
    shipFromSupplierCode: "Ship-from supplier number",
    deliveringFreightForwarderName: "Delivering freight forwarder name",
    deliveringFreightForwarderAddressCountry: "Delivering freight forwarder country",
    deliveringFreightForwarderAddressPostalCode: "Delivering freight forwarder zip code",
    deliveringFreightForwarderAddressCity: "Delivering freight forwarder city",
    deliveringFreightForwarderAddressStreet: "Delivering freight forwarder street",
    deliveringFreightForwarderDuns: "Delivering freight forwarder DUNS",
    deliveringFreightForwarderSupplierCode: "Delivering freight forwarder supplier number",
    calculatedArrivalAtCrossDock: "Calculated arrival at cross-dock",
    shipToName: "Ship-to name",
    shipToAddressCountry: "Ship-to country",
    shipToAddressPostalCode: "Ship-to zip code",
    shipToAddressCity: "Ship-to city",
    shipToAddressStreet: "Ship-to street",
    shipToDuns: "Ship-to DUNS",
    shipToSupplierCode: "Ship-to supplier number",
    shipToPlantCode: "Ship-to plant code",
    shipToPlaceOfDischarge: "Ship-to place of discharge",
    productNumbers: "Product numbers",
    lengthHandlingUnit: "Length [mm]",
    widthHandlingUnit: "Width [mm]",
    heightHandlingUnit: "Height [mm]",
    totalNumberOfPieces: "Number of pieces",
    handlingUnitQuantity: "Number of handling units",
    outerPackagingMaterialId: "Packaging material",
    unDangerousGoodsCode: "Dangerous goods code UN",
    descriptionDangerousGoods: "Dangerous goods description",
    dangerousGoodsClass: "Dangerous goods class",
    dangerousGoodsPackingGroup: "Dangerous goods packaging type",
    netExplosiveWeight: "Net explosive weight [g]",
    customsGoods: "Customs goods",
    grossWeight: "Gross weight [kg]",
    grossVolume: "Gross volume [m3]",
    stackingFactor: "Stacking factor",
}

export const getInboundMaterialCsvHeaders = (): InboundCsvHeader<InboundMaterialCsvExportRow>[] =>
    (Object.entries(MATERIAL_HEADER_LABELS) as [keyof InboundMaterialCsvExportRow, string][])
        .map(([key, label]) => ({ key, label }))

const hasInboundCustomsGoods = (articles: Article[]): string =>
    articles.some(article => article.country_of_origin?.dutiable_goods === true) ? "Yes" : "No"

const joinArticleValues = (
    articles: Article[],
    getValue: (article: Article) => string | number | null | undefined,
    fallback = "",
): string | undefined => {
    if (articles.length === 0) {
        return undefined
    }
    return articles
        .map(article => getValue(article)?.toString() ?? fallback)
        .join(" / ")
}

const getTotalNumberOfPieces = (
    article: Article,
    handlingUnit: HandlingUnit | undefined,
): number | undefined => {
    if (article.quantity === undefined) {
        return undefined
    }
    if (handlingUnit?.quantity === undefined) {
        return article.quantity
    }
    return article.quantity * handlingUnit.quantity
}

const mapHandlingUnit = (
    handlingUnit: HandlingUnit | undefined,
    shipment: Shipment,
): InboundMaterialCsvExportRow => {
    const flattenedHandlingUnit = handlingUnit && pullUpArticles(handlingUnit)
    const articles = flattenedHandlingUnit?.content ?? []
    const productNumbers = joinArticleValues(articles, article => article.article_id)
    const totalNumberOfPieces = joinArticleValues(articles, article =>
        getTotalNumberOfPieces(article, handlingUnit)
    )
    const unDangerousGoodsCode = joinArticleValues(
        articles,
        article => article.dangerous_goods_information?.undg_code,
        "-",
    )
    const descriptionDangerousGoods = joinArticleValues(
        articles,
        article => article.dangerous_goods_information?.description,
        "-",
    )
    const dangerousGoodsClass = joinArticleValues(
        articles,
        article => article.dangerous_goods_information?.hazard_class,
        "-",
    )
    const dangerousGoodsPackingGroup = joinArticleValues(
        articles,
        article => article.dangerous_goods_information?.packing_group,
        "-",
    )
    const { currentTransportLeg, startOfTransportChain, endOfTransportChain, freightForwarder } = shipment

    const requestedPickupAtShipFrom = startOfTransportChain.pickupRequestedAt
    const estimatedDeliveryAtShipTo = endOfTransportChain.deliveryEstimatedAt
    const shipFrom = startOfTransportChain.consignor
    const shipTo = endOfTransportChain.consignee

    return {
        transportOrderNumber: shipment.external_identification,
        externalOrderCreatedDate: formatInboundCsvDate(shipment.createdDate, getClientTimezone()),
        pickupDate: formatInboundCsvDate(
            requestedPickupAtShipFrom?.time_from,
            getTimezoneOfCountry(shipFrom.address?.country),
        ),
        pickupFrom: formatInboundCsvTime(
            requestedPickupAtShipFrom?.time_from,
            getTimezoneOfCountry(shipFrom.address?.country),
        ),
        pickupTo: formatInboundCsvTime(
            requestedPickupAtShipFrom?.time_to,
            getTimezoneOfCountry(shipFrom.address?.country),
        ),
        deliveryDate: formatInboundCsvDate(
            estimatedDeliveryAtShipTo?.time_from,
            getTimezoneOfCountry(shipTo.address?.country),
        ),
        shipFromName: shipFrom.name,
        shipFromAddressCountry: shipFrom.address?.country,
        shipFromAddressPostalCode: shipFrom.address?.postal_code,
        shipFromAddressCity: shipFrom.address?.city,
        shipFromAddressStreet: shipFrom.address?.street,
        shipFromDuns: shipFrom.externalIdentifiers.duns,
        shipFromSupplierCode: shipFrom.externalIdentifiers.supplierCode,
        deliveringFreightForwarderName: freightForwarder.name,
        deliveringFreightForwarderAddressCountry: freightForwarder.address?.country,
        deliveringFreightForwarderAddressPostalCode: freightForwarder.address?.postal_code,
        deliveringFreightForwarderAddressCity: freightForwarder.address?.city,
        deliveringFreightForwarderAddressStreet: freightForwarder.address?.street,
        deliveringFreightForwarderDuns: freightForwarder.duns,
        deliveringFreightForwarderSupplierCode: freightForwarder.supplierCode,
        calculatedArrivalAtCrossDock: formatInboundCsvDate(
            currentTransportLeg.arrivalEstimatedAt?.time_to,
            getTimezoneOfCountry(currentTransportLeg.placeOfArrival.address?.country),
        ),
        shipToName: shipTo.name,
        shipToAddressCountry: shipTo.address?.country,
        shipToAddressPostalCode: shipTo.address?.postal_code,
        shipToAddressCity: shipTo.address?.city,
        shipToAddressStreet: shipTo.address?.street,
        shipToDuns: shipTo.externalIdentifiers.duns,
        shipToSupplierCode: shipTo.externalIdentifiers.supplierCode,
        shipToPlantCode: shipTo.externalIdentifiers.plantCode,
        shipToPlaceOfDischarge: endOfTransportChain.placeOfDischarge,
        productNumbers,
        lengthHandlingUnit: normalizeToMmt(handlingUnit?.dimension?.length)?.value,
        widthHandlingUnit: normalizeToMmt(handlingUnit?.dimension?.width)?.value,
        heightHandlingUnit: normalizeToMmt(handlingUnit?.dimension?.height)?.value,
        totalNumberOfPieces,
        handlingUnitQuantity: handlingUnit?.quantity,
        outerPackagingMaterialId: handlingUnit?.packaging_material_id,
        unDangerousGoodsCode,
        descriptionDangerousGoods,
        dangerousGoodsClass,
        dangerousGoodsPackingGroup,
        netExplosiveWeight: handlingUnit && aggregateNetExplosiveMassInG(handlingUnit),
        customsGoods: hasInboundCustomsGoods(articles),
        grossWeight: handlingUnit && calculateWeight(handlingUnit),
        grossVolume: handlingUnit && calculateVolume(handlingUnit),
        stackingFactor: handlingUnit?.stacking_factor,
    }
}

export const mapInboundMaterialShipmentsToCsvRows = (
    shipments: Shipment[],
): InboundMaterialCsvExportRow[] =>
    shipments.flatMap(shipment => {
        if (!isCargoFreight(shipment.freight)) {
            return [mapHandlingUnit(undefined, shipment)]
        }
        return shipment.freight.handling_units.map(handlingUnit =>
            mapHandlingUnit(handlingUnit, shipment),
        )
    })
